// The feed's hero (31 Jul 2026): Discover's, over the articles instead of the
// practices. Same engine, same pauses, three deliberate differences.
//
// - The picture goes out to the source. Outbound is allowed on the feed
//   (spec section 5), and it is the honest action for an article: the feed's
//   job is to send people to what the experts published, not to keep the
//   traffic. A new tab, so the app is still there when they come back, which
//   also parks the slideshow, because leaving has always paused it.
// - It runs slower (31 Jul 2026, Rob). A headline is a different read from a
//   one-line instruction.
// - The byline dates to the year only: a feed hero is a display of what these
//   people publish, and a full date on a picture reads as filing.
import { useEffect, useRef, useState } from "react";
import { useStore } from "../state/store.js";
import type { FeedItemView } from "../state/types.js";
import { Avatar } from "./Avatar.js";
import { artFor } from "./placeholderArt.js";
import { HERO_SIZES, shuffled, useFitRow, useScrolledPast, useSlideshow, type Slide } from "./slideshow.js";
import { artImg } from "../assets.js";

interface FeedSlide extends Slide {
  expertId: string;
  kind: string | null;
  year: string | null;
  url: string;
}

const DECK_SIZE = 12;
// A little longer on every slide than the practice heroes get.
const FEED_PACE = 1.4;

// The feed row's own wording, kept in step: what you do with a podcast is not
// what you do with an article.
export function verbFor(kind: string | null): string {
  if (/podcast/i.test(kind ?? "")) return "Listen at";
  if (/video|tiktok/i.test(kind ?? "")) return "Watch at";
  return "Read at";
}

function hostOf(url: string): string {
  return url.replace(/^https?:\/\//, "").split("/")[0] ?? url;
}

export function yearOf(date: string | null): string | null {
  return /(\d{4})/.exec(date ?? "")?.[1] ?? null;
}

function toSlide(it: FeedItemView & { url: string }): FeedSlide {
  return {
    id: it.id,
    text: it.title,
    // No article art yet, so the item's first topic picks the stand-in, same
    // as the feed rows (29 Jul 2026).
    image: artFor({ image: it.image, topicId: it.topics[0] ?? null }),
    expertId: it.expertId,
    kind: it.kind,
    year: yearOf(it.date),
    url: it.url,
  };
}

// Same rule as Discover's: sampled from everything unread rather than taken
// off the top, and reordered so the same expert never runs twice.
export function spacedFeed(items: FeedSlide[]): FeedSlide[] {
  const rest = [...items];
  const out: FeedSlide[] = [];
  while (rest.length) {
    const last = out[out.length - 1];
    const i = Math.max(0, rest.findIndex((x) => !last || x.expertId !== last.expertId));
    out.push(...rest.splice(i, 1));
  }
  return out;
}

// No link, no slide: the picture is here to carry the reader out to the
// source, and a hero that goes nowhere is a poster.
export function heroCandidates(items: FeedItemView[]): Array<FeedItemView & { url: string }> {
  return items.filter((it): it is FeedItemView & { url: string } => Boolean(it.url));
}

function draw(items: Array<FeedItemView & { url: string }>, exclude: Set<string> = new Set(), room = DECK_SIZE) {
  const pool = items.filter((it) => !exclude.has(it.id)).map(toSlide);
  return spacedFeed(shuffled(pool).slice(0, room));
}

// Arriving from Today's Worth a look: the article the reader picked leads the
// deck (31 Jul 2026, Rob). Dealt into the deck rather than shown alone, so
// what follows is still the rest of their feed.
function withLead(deck: FeedSlide[], items: Array<FeedItemView & { url: string }>, lead: string | null | undefined) {
  if (!lead) return deck;
  const already = deck.find((s) => s.id === lead);
  const slide = already ?? (() => {
    const it = items.find((x) => x.id === lead);
    return it ? toSlide(it) : null;
  })();
  if (!slide) return deck;
  return [slide, ...deck.filter((s) => s.id !== lead)];
}

export function FeedHero({ items, lead }: {
  items: Array<FeedItemView & { url: string }>;
  // The item Today's Worth a look sent us to, if any.
  lead?: string | null;
}) {
  const experts = useStore((s) => s.experts);
  const nav = useStore((s) => s.nav);
  const sheetOpen = useStore((s) => s.sheet !== null);
  const stage = useRef<HTMLDivElement>(null);
  const whoRef = useRef<HTMLSpanElement>(null);
  const scrolledPast = useScrolledPast(stage);
  const [deck, setDeck] = useState<FeedSlide[]>(() => withLead(draw(items), items, lead));

  const ids = items.map((it) => it.id).join("|");
  useEffect(() => {
    setDeck((prev) => {
      const live = new Set(items.map((it) => it.id));
      const kept = prev.filter((s) => live.has(s.id));
      if (kept.length === prev.length && kept.length >= DECK_SIZE) return prev;
      const top = draw(items, new Set(kept.map((s) => s.id)), DECK_SIZE - kept.length);
      if (!top.length && kept.length === prev.length) return prev;
      return spacedFeed([...kept, ...top]);
    });
  }, [ids, items]);

  const { cur, prev, corner, show } = useSlideshow(deck, {
    paused: sheetOpen || scrolledPast,
    holdScale: FEED_PACE,
  });

  // A second arrival while the hero is already up: deal the article in if it
  // is not in the deck, then turn to it.
  useEffect(() => {
    if (!lead) return;
    setDeck((prev) => (prev.some((s) => s.id === lead) ? prev : withLead(prev, items, lead)));
    show(lead);
    window.scrollTo({ top: 0 });
    // items is read only to deal a missing slide; the lead is what moves.
  }, [lead]);
  useFitRow(whoRef, cur?.id);
  if (!cur) return null;
  const e = experts?.find((x) => x.id === cur.expertId);
  // The byline is the second door out of the picture (31 Jul 2026, Rob), and
  // it goes the other way: the photograph leaves for the source, the face and
  // the name stay here and open the person's page, exactly as they do on the
  // feed rows below. Both stop the click reaching the picture.
  const toExpert = (ev: React.MouseEvent) => {
    ev.stopPropagation();
    nav("expert", cur.expertId);
  };
  return (
    // The whole picture goes to the article, and the named link under the
    // headline says where that is.
    <div
      className="hslides apphero"
      ref={stage}
      onClick={() => window.open(cur.url, "_blank", "noopener,noreferrer")}
    >
      {prev && (
        <figure className="hslide out" key={`${prev.id}-out`} aria-hidden="true">
          <img className="hslide-img" alt="" {...artImg(prev.image)} sizes={HERO_SIZES} />
        </figure>
      )}
      <figure className="hslide" key={cur.id} data-pos={corner}>
        <img className="hslide-img" alt="" {...artImg(cur.image)} sizes={HERO_SIZES} />
        <figcaption>
          <span className="hslide-cap">
            {e && (
              <span className="hslide-who" ref={whoRef}>
                <button className="hslide-face" onClick={toExpert} aria-label={`Profile: ${e.name}`}>
                  <Avatar e={e} size="m" />
                </button>
                <span className="hslide-idn">
                  <button className="hslide-name" data-lead onClick={toExpert}>
                    {e.name}
                  </button>
                  <span className="hslide-meta" data-trail>
                    <span className="hslide-grade">{cur.kind ?? "Source"}</span>
                    {cur.year && (
                      <span className="hslide-time" data-drop>
                        {cur.year}
                      </span>
                    )}
                  </span>
                </span>
              </span>
            )}
            {/* A headline, not an instruction: titles run long where a
                practice is one line, so this one sets smaller and stops at
                three lines rather than filling the picture. */}
            <span className="hslide-do title">{cur.text}</span>
            {/* The link stays (31 Jul 2026, Rob): Discover's picture goes to
                a modal, so nothing on it needs naming, but this one leaves
                the app, and a reader is owed the destination before they
                press it. It is also the keyboard way out. */}
            <span className="hslide-cta">
              <a
                className="hslide-view"
                href={cur.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(ev) => ev.stopPropagation()}
              >
                {verbFor(cur.kind)} {hostOf(cur.url)} ↗
              </a>
            </span>
          </span>
        </figcaption>
      </figure>
    </div>
  );
}
